import React from "react";
import AlertV4 from "../common/ui/AlertV4";
import ButtonV4 from "../common/ui/ButtonV4";
import ModalV4 from "../common/ui/ModalV4";

/**
 * ถ่ายภาพด้วยกล้องสำหรับ Image Gallery ของ HIS V4
 *
 * V3 ใช้ `apps/common/WebcamComponent.tsx` (react-webcam + screenshotFormat image/jpeg)
 * V4 ใช้ getUserMedia + canvas โดยตรง เพื่อให้ไม่ต้องพึ่ง dependency ของ V3
 * และทดสอบใน jsdom ได้ (mock navigator.mediaDevices)
 *
 * ผลลัพธ์ต้องเป็น raster data URI เท่านั้น (backend ไม่รับ svg signed url)
 */

export type GalleryCameraV4Props = {
  /** ผู้ใช้กดปุ่ม ถ่ายด้วยกล้องแล้ว modalนี้ถึงจะ render */
  open: boolean;
  onClose: () => void;
  /** data:image/...;base64 ที่ capture ได้ (raster เท่านั้น) */
  onCapture: (image: string) => void;
  /** ข้อความกำกับภาพที่ผู้ใช้กำลังพิมพ์ (ใช้ต่อเมื่อผู้ใช้ยืนยันแล้วเท่านั้น) */
  caption?: string;
};

type CameraStatus = "idle" | "starting" | "ready" | "unsupported";

const MAX_SNAPSHOT_SIDE = 1280;

export const GalleryCameraV4: React.FC<GalleryCameraV4Props> = ({
  open,
  onClose,
  onCapture,
  caption = "",
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const [status, setStatus] = React.useState<CameraStatus>("idle");
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<string | null>(null);

  const stopStream = React.useCallback((): void => {
    if (streamRef.current !== null) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!open) {
      stopStream();
      setStatus("idle");
      setSnapshot(null);
      setErrorText(null);
      return;
    }

    let cancelled = false;

    const start = async (): Promise<void> => {
      const media = typeof navigator === "undefined" ? undefined : navigator.mediaDevices;

      if (media === undefined || typeof media.getUserMedia !== "function") {
        if (!cancelled) {
          setStatus("unsupported");
          setErrorText("เบราว์เซอร์นี้ไม่รองรับการถ่ายด้วยกล้อง (getUserMedia)");
        }
        return;
      }

      setStatus("starting");
      setErrorText(null);

      try {
        const stream = await media.getUserMedia({ video: { facingMode: "user" }, audio: false });
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        if (videoRef.current !== null) {
          videoRef.current.srcObject = stream;
          videoRef.current.play?.().catch(() => undefined);
        }
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        const name = `${((error as { name?: unknown })?.name ?? "")}`;
        setStatus("unsupported");
        setErrorText(
          name === "NotAllowedError"
            ? "ผู้ใช้ไม่อนุญาตให้เข้าถึงกล้องในเบราว์เซอร์"
            : name === "NotFoundError"
            ? "ไม่พบกล้องในเครื่องนี้"
            : "เปิดกล้องไม่ได้",
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, stopStream]);

  const takeSnapshot = (): void => {
    const video = videoRef.current;
    if (video === null) return;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const scale = Math.min(1, MAX_SNAPSHOT_SIDE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const context = canvas.getContext("2d");
    if (context === null) {
      setErrorText("browser ไม่ให้ใช้ canvas เพื่อบันทึกภาพจากกล้อง");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      /* jpeg อย่างเดียวกับ V3 (screenshotFormat="image/jpeg") */
      setSnapshot(canvas.toDataURL("image/jpeg"));
    } catch (error) {
      setErrorText("บันทึกภาพจากกล้องไม่สำเร็จ (canvas ถูกปิดกั้นโดย browser)");
    }
  };

  const confirm = (): void => {
    if (snapshot === null) return;
    onCapture(snapshot);
    onClose();
  };

  if (!open) return null;

  return (
    <ModalV4 title="ถ่ายภาพด้วยกล้อง" onClose={onClose}>
      {caption !== "" && <div className="hv4-mt-8">ชื่อภาพ: {caption}</div>}

      {snapshot === null ? (
        <React.Fragment>
          <video
            ref={videoRef}
            className="hv4-camera-video"
            width={320}
            height={240}
            muted
            playsInline
            autoPlay
          />
          {status === "starting" && <AlertV4 tone="info" className="hv4-mt-8">กำลังเปิดกล้อง…</AlertV4>}
          {errorText !== null && (
            <AlertV4 tone="danger" className="hv4-mt-8" onClose={() => setErrorText(null)}>
              {errorText}
            </AlertV4>
          )}
          <div className="hv4-mt-10">
            <ButtonV4
              variant="primary"
              className="hv4-btn-camera-capture"
              disabled={status !== "ready"}
              onClick={takeSnapshot}
            >
              ถ่ายภาพ
            </ButtonV4>
            <ButtonV4 variant="ghost" className="hv4-btn-camera-close" onClick={onClose}>
              ปิด
            </ButtonV4>
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div className="hv4-mt-8">
            <img className="hv4-camera-preview" src={snapshot} alt="ภาพที่ถ่ายด้วยกล้อง" />
          </div>
          <div className="hv4-mt-10">
            <ButtonV4 variant="confirm" className="hv4-btn-camera-confirm" onClick={confirm}>
              ใช้ภาพนี้
            </ButtonV4>
            <ButtonV4 variant="ghost" className="hv4-btn-camera-again" onClick={() => setSnapshot(null)}>
              ถ่ายใหม่
            </ButtonV4>
            <ButtonV4 variant="ghost" className="hv4-btn-camera-close" onClick={onClose}>
              ปิด
            </ButtonV4>
          </div>
        </React.Fragment>
      )}
    </ModalV4>
  );
};

export default GalleryCameraV4;
